/**
 * Mock Data for BrandHealth 360 Frontend Testing
 * This file provides realistic mock data for all API endpoints
 */

import type {
  Brand,
  EquityData,
  FunnelData,
  ChannelData,
  PositioningData,
  DigitalMetrics,
  SentimentData,
  LoyaltyData,
  FilterOptions
} from './brandHealthServiceV2';

import type { ChatSession, ChatMessage, ChatSessionDetail } from './chatService';

// Mock Brands
export const mockBrands: Brand[] = [
  { brand_id: 'coca-cola', name: 'Coca-Cola', is_digital: false },
  { brand_id: 'pepsi', name: 'Pepsi', is_digital: false },
  { brand_id: 'sprite', name: 'Sprite', is_digital: false },
  { brand_id: 'fanta', name: 'Fanta', is_digital: false },
  { brand_id: 'mountain-dew', name: 'Mountain Dew', is_digital: false },
];

// Mock Latest Equity Data
export const mockLatestEquity: EquityData[] = [
  {
    org_id: 'demo',
    date: '2024-12-01',
    brand_id: 'coca-cola',
    trad_equity: 85.5,
    digital_equity: 78.2,
    total_equity: 81.9,
    brand_index: 88.3,
    community_index: 75.6,
    is_digital: false
  },
  {
    org_id: 'demo',
    date: '2024-12-01',
    brand_id: 'pepsi',
    trad_equity: 76.8,
    digital_equity: 82.4,
    total_equity: 79.6,
    brand_index: 80.2,
    community_index: 79.0,
    is_digital: false
  },
  {
    org_id: 'demo',
    date: '2024-12-01',
    brand_id: 'sprite',
    trad_equity: 68.3,
    digital_equity: 71.5,
    total_equity: 69.9,
    brand_index: 72.1,
    community_index: 67.7,
    is_digital: false
  },
  {
    org_id: 'demo',
    date: '2024-12-01',
    brand_id: 'fanta',
    trad_equity: 62.7,
    digital_equity: 65.8,
    total_equity: 64.3,
    brand_index: 66.5,
    community_index: 62.1,
    is_digital: false
  },
  {
    org_id: 'demo',
    date: '2024-12-01',
    brand_id: 'mountain-dew',
    trad_equity: 71.2,
    digital_equity: 76.9,
    total_equity: 74.1,
    brand_index: 75.8,
    community_index: 72.4,
    is_digital: false
  },
];

// Generate equity trends over 6 months
function generateEquityTrends(months: number = 6): { [brand_id: string]: EquityData[] } {
  const trends: { [brand_id: string]: EquityData[] } = {};
  const today = new Date();

  mockBrands.forEach(brand => {
    trends[brand.brand_id] = [];
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setMonth(date.getMonth() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Add some variation over time
      const baseEquity = mockLatestEquity.find(e => e.brand_id === brand.brand_id)!;
      const variation = (Math.random() - 0.5) * 10;

      trends[brand.brand_id].push({
        org_id: 'demo',
        date: dateStr,
        brand_id: brand.brand_id,
        trad_equity: Math.max(0, Math.min(100, baseEquity.trad_equity + variation)),
        digital_equity: Math.max(0, Math.min(100, baseEquity.digital_equity + variation)),
        total_equity: Math.max(0, Math.min(100, baseEquity.total_equity + variation)),
        brand_index: Math.max(0, Math.min(100, baseEquity.brand_index + variation)),
        community_index: Math.max(0, Math.min(100, baseEquity.community_index + variation)),
        is_digital: baseEquity.is_digital
      });
    }
  });

  return trends;
}

export const mockEquityTrends = generateEquityTrends(6);

// Mock Awareness Funnel Data
export const mockAwarenessFunnel: FunnelData = {
  total_respondents: 1250,
  filters: {
    brand_id: null,
    gender: null,
    age: null,
    city: null,
    nationality: null,
    sec: null
  },
  funnel: {
    'coca-cola': {
      'Top of Mind': { count: 687, percentage: 55.0 },
      'Unaided awareness': { count: 950, percentage: 76.0 },
      'Aided awareness': { count: 1188, percentage: 95.0 },
      'Consideration': { count: 875, percentage: 70.0 },
      'Purchase': { count: 625, percentage: 50.0 }
    },
    'pepsi': {
      'Top of Mind': { count: 425, percentage: 34.0 },
      'Unaided awareness': { count: 825, percentage: 66.0 },
      'Aided awareness': { count: 1125, percentage: 90.0 },
      'Consideration': { count: 750, percentage: 60.0 },
      'Purchase': { count: 500, percentage: 40.0 }
    },
    'sprite': {
      'Top of Mind': { count: 238, percentage: 19.0 },
      'Unaided awareness': { count: 625, percentage: 50.0 },
      'Aided awareness': { count: 1000, percentage: 80.0 },
      'Consideration': { count: 500, percentage: 40.0 },
      'Purchase': { count: 313, percentage: 25.0 }
    },
    'fanta': {
      'Top of Mind': { count: 175, percentage: 14.0 },
      'Unaided awareness': { count: 500, percentage: 40.0 },
      'Aided awareness': { count: 875, percentage: 70.0 },
      'Consideration': { count: 375, percentage: 30.0 },
      'Purchase': { count: 250, percentage: 20.0 }
    },
    'mountain-dew': {
      'Top of Mind': { count: 313, percentage: 25.0 },
      'Unaided awareness': { count: 700, percentage: 56.0 },
      'Aided awareness': { count: 1063, percentage: 85.0 },
      'Consideration': { count: 563, percentage: 45.0 },
      'Purchase': { count: 375, percentage: 30.0 }
    }
  }
};

// Mock Channel Distribution
export const mockChannels: ChannelData = {
  'coca-cola': [
    { channel: 'TV', count: 450 },
    { channel: 'Social Media', count: 380 },
    { channel: 'Radio', count: 220 },
    { channel: 'Print', count: 150 },
    { channel: 'Outdoor', count: 180 }
  ],
  'pepsi': [
    { channel: 'TV', count: 380 },
    { channel: 'Social Media', count: 420 },
    { channel: 'Radio', count: 180 },
    { channel: 'Print', count: 120 },
    { channel: 'Outdoor', count: 140 }
  ],
  'sprite': [
    { channel: 'TV', count: 250 },
    { channel: 'Social Media', count: 310 },
    { channel: 'Radio', count: 140 },
    { channel: 'Print', count: 90 },
    { channel: 'Outdoor', count: 110 }
  ]
};

// Mock Positioning Data
export const mockPositioning: PositioningData = {
  'coca-cola': [
    { statement: 'Refreshing', count: 520 },
    { statement: 'Classic', count: 480 },
    { statement: 'Iconic', count: 450 },
    { statement: 'Trusted', count: 420 },
    { statement: 'Authentic', count: 380 }
  ],
  'pepsi': [
    { statement: 'Modern', count: 460 },
    { statement: 'Youthful', count: 440 },
    { statement: 'Bold', count: 410 },
    { statement: 'Innovative', count: 390 },
    { statement: 'Energetic', count: 360 }
  ],
  'sprite': [
    { statement: 'Refreshing', count: 380 },
    { statement: 'Crisp', count: 350 },
    { statement: 'Clean', count: 320 },
    { statement: 'Light', count: 290 },
    { statement: 'Natural', count: 260 }
  ]
};

// Mock Digital Metrics
export const mockDigitalMetrics: DigitalMetrics[] = [
  {
    brand_id: 'coca-cola',
    channel: 'Facebook',
    total_posts: 1250,
    total_likes: 450000,
    total_comments: 85000,
    total_shares: 32000,
    total_interactions: 567000,
    total_engagements: 650000,
    avg_followers: 12500000
  },
  {
    brand_id: 'coca-cola',
    channel: 'Instagram',
    total_posts: 980,
    total_likes: 850000,
    total_comments: 125000,
    total_shares: 0,
    total_interactions: 975000,
    total_engagements: 1100000,
    avg_followers: 8900000
  },
  {
    brand_id: 'pepsi',
    channel: 'Facebook',
    total_posts: 1100,
    total_likes: 380000,
    total_comments: 72000,
    total_shares: 28000,
    total_interactions: 480000,
    total_engagements: 550000,
    avg_followers: 10200000
  },
  {
    brand_id: 'pepsi',
    channel: 'Instagram',
    total_posts: 1150,
    total_likes: 720000,
    total_comments: 98000,
    total_shares: 0,
    total_interactions: 818000,
    total_engagements: 920000,
    avg_followers: 7600000
  }
];

// Mock Sentiment Data
export const mockSentiment: SentimentData = {
  'coca-cola': {
    'positive': { count: 5620, avg_score: 0.82 },
    'neutral': { count: 1840, avg_score: 0.05 },
    'negative': { count: 340, avg_score: -0.68 }
  },
  'pepsi': {
    'positive': { count: 4890, avg_score: 0.78 },
    'neutral': { count: 2150, avg_score: 0.03 },
    'negative': { count: 460, avg_score: -0.72 }
  },
  'sprite': {
    'positive': { count: 3120, avg_score: 0.75 },
    'neutral': { count: 1280, avg_score: 0.04 },
    'negative': { count: 280, avg_score: -0.65 }
  }
};

// Mock Loyalty Data
export const mockLoyalty: LoyaltyData[] = [
  {
    brand_id: 'coca-cola',
    total_users: 1250,
    avg_loyalty: 78.5,
    avg_refer_score: 8.2,
    nps: 52,
    promoters: 687,
    detractors: 125
  },
  {
    brand_id: 'pepsi',
    total_users: 1250,
    avg_loyalty: 72.3,
    avg_refer_score: 7.5,
    nps: 38,
    promoters: 563,
    detractors: 188
  },
  {
    brand_id: 'sprite',
    total_users: 1250,
    avg_loyalty: 65.8,
    avg_refer_score: 6.8,
    nps: 28,
    promoters: 438,
    detractors: 213
  }
];

// Mock Filter Options
export const mockFilterOptions: FilterOptions = {
  genders: ['Male', 'Female', 'Other'],
  age_groups: ['18-24', '25-34', '35-44', '45-54', '55+'],
  cities: ['Cairo', 'Alexandria', 'Giza', 'Dubai', 'Riyadh', 'Jeddah'],
  channels: ['TV', 'Social Media', 'Radio', 'Print', 'Outdoor', 'Digital', 'OOH']
};

// Mock Awareness Questions
export const mockAwarenessQuestions = [
  'Top of Mind',
  'Unaided awareness',
  'Aided awareness',
  'Consideration',
  'Purchase'
];

// Mock Positioning Questions
export const mockPositioningQuestions = [
  'Refreshing',
  'Modern',
  'Classic',
  'Youthful',
  'Trusted',
  'Innovative',
  'Bold',
  'Authentic'
];

// Mock Channel List
export const mockChannelList = ['Facebook', 'Instagram', 'Twitter', 'TikTok', 'YouTube'];

// Mock Social Channels
export const mockSocialChannels = ['Facebook', 'Instagram', 'Twitter', 'TikTok', 'LinkedIn', 'YouTube'];

// Mock Google Trends
export function generateGoogleTrends(months: number = 6): { [brand: string]: { date: string; score: number }[] } {
  const trends: { [brand: string]: { date: string; score: number }[] } = {};
  const today = new Date();

  mockBrands.forEach(brand => {
    trends[brand.name] = [];
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setMonth(date.getMonth() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Generate trend score (0-100) with some variation
      const baseScore = brand.brand_id === 'coca-cola' ? 85 :
                       brand.brand_id === 'pepsi' ? 75 :
                       brand.brand_id === 'mountain-dew' ? 70 : 65;
      const score = Math.max(0, Math.min(100, baseScore + (Math.random() - 0.5) * 20));

      trends[brand.name].push({ date: dateStr, score });
    }
  });

  return trends;
}

export const mockGoogleTrends = generateGoogleTrends(6);

// === CHAT MOCK DATA ===

export const mockChatSessions: ChatSession[] = [
  {
    id: 'chat_001',
    title: 'Q4 Performance Analysis',
    brand_id: 'coca-cola',
    last_message: 'The Q4 performance shows strong growth in digital engagement...',
    message_count: 8,
    created_at: '2024-12-15T10:30:00Z',
    updated_at: '2024-12-15T11:45:00Z'
  },
  {
    id: 'chat_002',
    title: 'Social Media Strategy',
    brand_id: 'pepsi',
    last_message: 'Instagram engagement is outperforming Facebook by 23%...',
    message_count: 5,
    created_at: '2024-12-14T14:20:00Z',
    updated_at: '2024-12-14T15:30:00Z'
  },
  {
    id: 'chat_003',
    title: 'Brand Positioning Insights',
    brand_id: 'coca-cola',
    last_message: 'The "Refreshing" attribute resonates most with 25-34 age group...',
    message_count: 12,
    created_at: '2024-12-13T09:15:00Z',
    updated_at: '2024-12-13T16:20:00Z'
  },
  {
    id: 'chat_004',
    title: 'Competitor Analysis',
    last_message: 'Compared to competitors, our NPS is 14 points higher...',
    message_count: 6,
    created_at: '2024-12-12T11:00:00Z',
    updated_at: '2024-12-12T12:30:00Z'
  },
  {
    id: 'chat_005',
    title: 'Campaign Effectiveness',
    brand_id: 'sprite',
    last_message: 'The summer campaign drove a 32% increase in awareness...',
    message_count: 9,
    created_at: '2024-12-10T13:45:00Z',
    updated_at: '2024-12-10T15:00:00Z'
  }
];

export const mockChatMessages: { [chatId: string]: ChatMessage[] } = {
  'chat_001': [
    {
      role: 'user',
      content: 'What was our Q4 performance like for Coca-Cola?',
      timestamp: '2024-12-15T10:30:00Z'
    },
    {
      role: 'assistant',
      content: 'Based on the Q4 data, Coca-Cola showed strong performance:\n\n**Key Highlights:**\n- Total Equity: 81.9 (up 3.2% from Q3)\n- Brand Index: 88.3 (industry leading)\n- NPS: 52 (excellent customer loyalty)\n- Digital Engagement: +23% YoY\n\nThe brand maintains its market leader position with particularly strong performance in traditional equity (85.5) and growing digital presence (78.2).',
      timestamp: '2024-12-15T10:31:15Z'
    },
    {
      role: 'user',
      content: 'How does that compare to Pepsi?',
      timestamp: '2024-12-15T10:32:00Z'
    },
    {
      role: 'assistant',
      content: '**Coca-Cola vs Pepsi Comparison:**\n\n| Metric | Coca-Cola | Pepsi | Difference |\n|--------|-----------|-------|------------|\n| Total Equity | 81.9 | 79.6 | +2.3 |\n| Brand Index | 88.3 | 80.2 | +8.1 |\n| NPS | 52 | 38 | +14 |\n| Digital Equity | 78.2 | 82.4 | -4.2 |\n\nCoca-Cola leads in overall brand strength and customer loyalty, while Pepsi has a slight edge in digital equity, reflecting their strong social media presence.',
      timestamp: '2024-12-15T10:32:45Z'
    }
  ],
  'chat_002': [
    {
      role: 'user',
      content: 'Show me Pepsi\'s social media performance',
      timestamp: '2024-12-14T14:20:00Z'
    },
    {
      role: 'assistant',
      content: '**Pepsi Social Media Performance:**\n\n**Instagram** (Best Performer):\n- Posts: 1,150\n- Total Likes: 720K\n- Engagement Rate: 12.1%\n- Followers: 7.6M\n\n**Facebook**:\n- Posts: 1,100\n- Total Likes: 380K\n- Engagement Rate: 5.4%\n- Followers: 10.2M\n\nInstagram is outperforming Facebook by 23% in engagement rate, suggesting the younger demographic is more engaged with Pepsi\'s visual content.',
      timestamp: '2024-12-14T14:21:30Z'
    }
  ]
};

// Generate detailed chat session from chat ID
export function getMockChatSession(chatId: string): ChatSessionDetail {
  const session = mockChatSessions.find(s => s.id === chatId);
  if (!session) {
    throw new Error(`Chat session ${chatId} not found`);
  }

  return {
    id: session.id,
    title: session.title,
    brand_id: session.brand_id,
    messages: mockChatMessages[chatId] || [],
    created_at: session.created_at,
    updated_at: session.updated_at
  };
}
